/**
 * Dynamic values (requirements, Feature 3): date rules and name tokens that are
 * resolved at the moment a template is applied.
 *
 * A date cell can hold a rule instead of a date:
 *   { base: 'apply', offset: 3, unit: 'days' | 'working_days' }
 *   Today / Tomorrow / Yesterday are offsets 0 / 1 / -1 in days. Working days are
 *   Monday–Friday; from a weekend, +1 working day is Monday. No holidays.
 *   { base: 'parent', column: { title, type, part? }, offset, unit }
 *   counts from a date on the PARENT item instead: a Date column, or the start
 *   or end (`part`) of a Timeline column, named by title and type the way
 *   columns are matched everywhere else. The parent item is the item the
 *   subitems are created under: the current item for a subitem template, the
 *   item row for a group template's subitems.
 * Stored on the cell as `dynamic`, beside the usual { id, type, value, text }
 * with value null — see columnValues.js.
 *
 * A timeline cell can hold a rule for each end:
 *   { start: { base: 'apply' | 'parent', ... },
 *     end:   { base: 'start' | 'apply' | 'parent', offset, unit, column? } }
 * "Today + 0 days → Start + 4 working days" is a five-working-day span
 * starting the day the template is applied ("Today" in the UI is always the
 * apply date). An end that would fall before the start is pulled up to the
 * start. Next week / Next month are Today → Today + 7 / + 30 days, exact
 * counts, not calendar weeks or months.
 *
 * A People cell can copy the parent's People column, beside the people picked:
 *   dynamic: { base: 'parent', column: { title, type: 'people' }, alsoPicked }
 * People and teams in the parent's column are both copied; with alsoPicked the
 * cell's own picks are added. An empty parent column leaves the picks alone;
 * nothing at all leaves the cell empty.
 *
 * A name can hold the `{date}` token, printed as "Oct 12, 2026" at apply, and
 * parent tokens: `{parent:name}` for the parent's name, `{parent:<type>:<title>}`
 * for a column's text (the title URL-encoded, so any title fits). A parent
 * token with no value prints nothing.
 *
 * Everything resolves once per apply against one context: { baseDate, parent }
 * — today in the applier's timezone (applyBaseDate) and the parent item
 * (parentFromCells / lib/parentItem), or null where there is none. A rule
 * whose parent date is empty or whose column is not on the parent resolves to
 * nothing, and the cell is left empty. The resolvers also take a bare base
 * date, as before. All date arithmetic is in UTC on 'YYYY-MM-DD' strings, so
 * DST never shifts a day.
 *
 * Mirrored in spt/src/helpers/dynamic-values.ts, fixture for fixture.
 */
import { normalizeTitle, typesMatch } from './columnMatching';

const DAY_MS = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const DATE_UNITS = Object.freeze(['days', 'working_days']);
export const DATE_BASES = Object.freeze(['apply', 'parent']);
/** The parent columns a rule can count from. */
export const PARENT_DATE_TYPES = Object.freeze(['date', 'timeline']);
export const MAX_OFFSET = 365;
export const DATE_TOKEN = '{date}';
export const PARENT_NAME_TOKEN = '{parent:name}';
/** The parent column types a name can carry; a date prints as "Nov 20, 2026", the rest as monday's text. */
export const NAME_TOKEN_TYPES = Object.freeze(['text', 'status', 'dropdown', 'numbers', 'date', 'people']);
/** Every parent token: the name, or a column by type and URL-encoded title. */
export const PARENT_TOKEN_SRC = '\\{parent:(?:name|([a-z_-]+):([^{}\\s]*))\\}';
export const NAME_MAX_CHARS = 255;
export const UNTITLED = 'Untitled';

export const DYNAMIC_PRESETS = Object.freeze([
  { key: 'today', label: 'Today', rule: { base: 'apply', offset: 0, unit: 'days' } },
  { key: 'tomorrow', label: 'Tomorrow', rule: { base: 'apply', offset: 1, unit: 'days' } },
  { key: 'yesterday', label: 'Yesterday', rule: { base: 'apply', offset: -1, unit: 'days' } },
]);

const pad = (n) => String(n).padStart(2, '0');

function parseIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(ms) ? ms : null;
}

function toIso(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** { title, type: 'date' | 'timeline', part?: 'start' | 'end' } — a timeline column names its end. */
export function isParentColumn(c) {
  if (!c || typeof c !== 'object' || typeof c.title !== 'string' || !c.title.trim()) return false;
  if (c.type === 'date') return c.part === undefined;
  if (c.type === 'timeline') return c.part === 'start' || c.part === 'end';
  return false;
}

const offsetOk = (r) => Number.isInteger(r.offset) && Math.abs(r.offset) <= MAX_OFFSET && DATE_UNITS.includes(r.unit);

export function isDynamicRule(rule) {
  if (!rule || typeof rule !== 'object' || !DATE_BASES.includes(rule.base) || !offsetOk(rule)) return false;
  return rule.base === 'parent' ? isParentColumn(rule.column) : true;
}

/**
 * The resolution context: the apply date and the parent item, or null. A bare
 * 'YYYY-MM-DD' is the apply date with no parent, so every caller that resolves
 * against today alone keeps working.
 */
const toCtx = (ctx) => (ctx && typeof ctx === 'object' ? ctx : { baseDate: ctx, parent: null });

/**
 * The parent's cell a rule or token names, matched by title and type like
 * every other column: { title, type, value, text } or null. `parent` is
 * { name, columns: [...] } (parentFromCells / lib/parentItem).
 */
export function parentCell(parent, column) {
  if (!parent || !column) return null;
  const title = normalizeTitle(column.title);
  return (parent.columns || []).find((c) => normalizeTitle(c.title) === title && typesMatch(c.type, column.type)) || null;
}

/** The parent date a rule counts from ('YYYY-MM-DD'), or null when empty or missing. */
export function parentDate(parent, column) {
  const cell = parentCell(parent, column);
  if (!cell || !cell.value) return null;
  let v;
  try {
    v = JSON.parse(cell.value);
  } catch (e) {
    return null;
  }
  const iso = v && (column.type === 'timeline' ? (column.part === 'start' ? v.from : v.to) : v.date);
  return parseIso(iso) === null ? null : iso;
}

const baseFor = (rule, ctx) => (rule.base === 'parent' ? parentDate(ctx.parent, rule.column) : ctx.baseDate);

/** `{parent:people:Account%20manager}` for a column; the title URL-encoded so braces, colons and spaces fit. */
export function parentToken(column) {
  return `{parent:${column.type}:${encodeURIComponent(column.title)}}`;
}

/** { kind: 'name' } | { kind: 'column', column: { type, title } } | null for anything else. */
export function parseParentToken(token) {
  const m = new RegExp(`^${PARENT_TOKEN_SRC}$`).exec(String(token || ''));
  if (!m) return null;
  if (!m[1]) return { kind: 'name' };
  try {
    return { kind: 'column', column: { type: m[1], title: decodeURIComponent(m[2]) } };
  } catch (e) {
    return null;
  }
}

/** What a parent column prints as in a name: a date as "Nov 20, 2026", the rest as monday's text; '' when empty. */
export function parentText(parent, column) {
  const cell = parentCell(parent, column);
  if (!cell) return '';
  if (typesMatch(cell.type, 'date')) {
    try {
      const v = JSON.parse(cell.value);
      return v && v.date ? formatDate(v.date) : '';
    } catch (e) {
      return '';
    }
  }
  return cell.text || '';
}

/** { base: 'parent', column: { title, type: 'people' }, alsoPicked }: copy the parent's People column. */
export function isPeopleRule(rule) {
  return Boolean(
    rule && typeof rule === 'object' && rule.base === 'parent' && rule.column && rule.column.type === 'people' && typeof rule.column.title === 'string' && rule.column.title.trim(),
  );
}

/** The people and teams in a People cell's stored value: [{ id, kind }]. */
export function peopleIn(json) {
  let v;
  try {
    v = JSON.parse(json);
  } catch (e) {
    return [];
  }
  const list = v && Array.isArray(v.personsAndTeams) ? v.personsAndTeams : [];
  return list.filter((p) => p && p.id !== null && p.id !== undefined).map((p) => ({ id: p.id, kind: p.kind === 'team' ? 'team' : 'person' }));
}

/**
 * A People rule on a cell, resolved: the parent column's people and teams,
 * then the cell's own picks when alsoPicked, each once. { value, text } for
 * the cell, or null when nobody is left (the cell is left empty).
 */
export function resolvePeople(rule, parent, own) {
  const cell = parentCell(parent, rule.column);
  const fromParent = cell ? peopleIn(cell.value) : [];
  const picked = rule.alsoPicked ? peopleIn(own && own.value) : [];
  const seen = new Set();
  const merged = [...fromParent, ...picked].filter((p) => {
    const k = `${p.kind}:${p.id}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  if (!merged.length) return null;
  const text = [cell && cell.text, rule.alsoPicked && own ? own.text : ''].filter(Boolean).join(', ');
  return { value: JSON.stringify({ personsAndTeams: merged }), text };
}

export function addDays(iso, n) {
  const ms = parseIso(iso);
  return ms === null ? null : toIso(ms + n * DAY_MS);
}

export function addWorkingDays(iso, n) {
  let ms = parseIso(iso);
  if (ms === null) return null;
  const step = n < 0 ? -1 : 1;
  let left = Math.abs(n);
  while (left > 0) {
    ms += step * DAY_MS;
    const dow = new Date(ms).getUTCDay();
    if (dow !== 0 && dow !== 6) left -= 1;
  }
  return toIso(ms);
}

const shift = (iso, rule) => (iso ? (rule.unit === 'working_days' ? addWorkingDays(iso, rule.offset) : addDays(iso, rule.offset)) : null);

/** The date a rule lands on for a context (or a bare base date), or null for a bad rule, date or missing parent. */
export function resolveDate(rule, ctx) {
  if (!isDynamicRule(rule)) return null;
  return shift(baseFor(rule, toCtx(ctx)), rule);
}

export const TIMELINE_END_BASES = Object.freeze(['start', 'apply', 'parent']);

/** The rule a new dynamic timeline starts from: five working days from the apply date. */
export const DEFAULT_TIMELINE_RULE = Object.freeze({
  start: Object.freeze({ base: 'apply', offset: 0, unit: 'days' }),
  end: Object.freeze({ base: 'start', offset: 4, unit: 'working_days' }),
});

const span = (days) =>
  Object.freeze({
    start: Object.freeze({ base: 'apply', offset: 0, unit: 'days' }),
    end: Object.freeze({ base: 'apply', offset: days, unit: 'days' }),
  });

export const TIMELINE_PRESETS = Object.freeze([
  { key: 'next_week', label: 'Next week', rule: span(7) },
  { key: 'next_month', label: 'Next month', rule: span(30) },
]);

/**
 * The preset a timeline rule is, or null. Matched on what it resolves to:
 * a start of Today and an end N days on, whether the end counts from Start or
 * from Today (the same day when the start is Today).
 */
export function timelinePreset(rule) {
  // only a rule counted from the apply date is a preset; one from the parent item never is
  if (!isTimelineRule(rule) || rule.start.base !== 'apply' || rule.start.offset !== 0) return null;
  const { end } = rule;
  if (end.base !== 'start' && end.base !== 'apply') return null;
  return TIMELINE_PRESETS.find((p) => end.unit === 'days' && end.offset === p.rule.end.offset) || null;
}

export function isTimelineRule(rule) {
  const end = rule && rule.end;
  if (!rule || typeof rule !== 'object' || !isDynamicRule(rule.start)) return false;
  if (!end || typeof end !== 'object' || !TIMELINE_END_BASES.includes(end.base) || !offsetOk(end)) return false;
  return end.base === 'parent' ? isParentColumn(end.column) : true;
}

/** { from, to } ('YYYY-MM-DD') for a timeline rule and a context (or base date), or null for a bad rule, date or missing parent. */
export function resolveTimeline(rule, ctx) {
  if (!isTimelineRule(rule)) return null;
  const c = toCtx(ctx);
  const from = resolveDate(rule.start, c);
  if (!from) return null;
  let to = shift(rule.end.base === 'start' ? from : baseFor(rule.end, c), rule.end);
  if (!to) return null;
  if (to < from) to = from; // ISO dates compare as strings
  return { from, to };
}

const offsetText = (r) => {
  const n = Math.abs(r.offset);
  const unit = r.unit === 'working_days' ? (n === 1 ? 'working day' : 'working days') : n === 1 ? 'day' : 'days';
  return `${r.offset < 0 ? '−' : '+'}${n} ${unit}`;
};

/** "Start date", "Timeline start": the parent column a rule names, as the editor and chips say it. */
export function parentColumnLabel(column) {
  if (!column) return '';
  return column.type === 'timeline' ? `${column.title} ${column.part}` : column.title;
}

/** "Today" for the apply date, "Parent's Start date" for a parent date, "Start" for a timeline's own start. */
const baseLabel = (r) => (r.base === 'parent' ? `Parent's ${parentColumnLabel(r.column)}` : r.base === 'start' ? 'Start' : 'Today');
const withOffset = (r) => (r.offset === 0 ? baseLabel(r) : `${baseLabel(r)} ${offsetText(r)}`);

/** "Next week", "Today → Start +4 working days", "Parent's Start date −5 working days → Start +3 days". */
export function describeTimeline(rule) {
  if (!isTimelineRule(rule)) return '';
  const preset = timelinePreset(rule);
  if (preset) return preset.label;
  return `${withOffset(rule.start)} → ${withOffset(rule.end)}`;
}

/** "Today", "Tomorrow", "Yesterday", "+3 working days", "−2 days", "Parent's Start date −5 working days". */
export function describeRule(rule) {
  if (!isDynamicRule(rule)) return '';
  if (rule.base === 'parent') return withOffset(rule);
  const preset = DYNAMIC_PRESETS.find((p) => p.rule.offset === rule.offset && p.rule.unit === rule.unit);
  if (preset) return preset.label;
  return offsetText(rule);
}

/** Does this rule, timeline rule or stored cell read the parent item? */
export function usesParent(x) {
  if (!x || typeof x !== 'object') return false;
  if (x.dynamic) return usesParent(x.dynamic);
  if (x.start) return x.start.base === 'parent' || (x.end && x.end.base === 'parent');
  return x.base === 'parent';
}

/** Does this name carry a parent token? */
export function nameUsesParent(name) {
  return new RegExp(PARENT_TOKEN_SRC).test(String(name == null ? '' : name));
}

/** Does any row's name or cell read the parent item? (Apply reads the parent only then.) */
export function rowsUseParent(rows) {
  return (rows || []).some((r) => nameUsesParent(r.name) || (r.column_values || []).some(usesParent));
}

/**
 * A parent context from a row's resolved cells: { name, columns: [{ title,
 * type, value, text }] }. `columns` names the cells' titles (a template's
 * column list); a cell whose column is unknown is left out.
 */
export function parentFromCells(name, cells, columns) {
  const titleOf = (id) => {
    const c = (columns || []).find((col) => String(col.id) === String(id));
    return c ? c.title : null;
  };
  return {
    name: String(name == null ? '' : name),
    columns: (cells || [])
      .map((cv) => (cv && titleOf(cv.id) ? { title: titleOf(cv.id), type: cv.type, value: cv.value, text: cv.text } : null))
      .filter(Boolean),
  };
}

/** 'YYYY-MM-DD' → 'Oct 12, 2026'; anything else is returned as given. */
export function formatDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return String(iso || '');
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}, ${Number(m[1])}` : String(iso);
}

/** Today as 'YYYY-MM-DD' in a timezone; an unknown zone falls back to UTC. */
export function applyBaseDate(timeZone, now = new Date()) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  } catch (e) {
    parts = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  }
  const get = (type) => (parts.find((p) => p.type === type) || {}).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Name with `{date}` and the parent tokens filled in. Other braces stay as
 * typed; an empty result is "Untitled"; the result is cut at 255 characters.
 * Without a date the date token is left as typed, and without a `parent` key
 * in the context so are the parent tokens (previews before apply pass them);
 * a parent of null, or a value the parent lacks, prints nothing.
 */
export function resolveName(name, ctx) {
  let out = String(name == null ? '' : name);
  if (ctx && ctx.date) out = out.split(DATE_TOKEN).join(formatDate(ctx.date));
  if (ctx && 'parent' in ctx) {
    out = out.replace(new RegExp(PARENT_TOKEN_SRC, 'g'), (token) => {
      const t = parseParentToken(token);
      if (!t) return token;
      if (!ctx.parent) return '';
      return t.kind === 'name' ? ctx.parent.name || '' : parentText(ctx.parent, t.column);
    });
  }
  out = out.trim();
  if (!out) out = UNTITLED;
  return out.slice(0, NAME_MAX_CHARS);
}

/**
 * A stored cell with `dynamic` → the same cell with fixed dates (a date rule →
 * { date }, a timeline rule → { from, to }); others untouched; an
 * unresolvable rule, or a parent date that is empty → null (the cell is left
 * empty).
 */
export function resolveColumnValue(cv, ctx) {
  if (!cv || !cv.dynamic) return cv;
  const { dynamic, ...rest } = cv;
  if (dynamic.start) {
    const t = resolveTimeline(dynamic, ctx);
    return t ? { ...rest, value: JSON.stringify(t), text: `${t.from} - ${t.to}` } : null;
  }
  if (isPeopleRule(dynamic)) {
    const people = resolvePeople(dynamic, toCtx(ctx).parent, rest);
    return people ? { ...rest, ...people } : null;
  }
  const date = resolveDate(dynamic, ctx);
  if (!date) return null;
  return { ...rest, value: JSON.stringify({ date }), text: date };
}

/**
 * The pre-pass both apply paths run once: every row's name and date rules
 * resolved against one context (or a bare base date), so the loops only ever
 * see fixed values.
 */
export function resolveSubitemsForApply(subitems, ctx) {
  const c = toCtx(ctx);
  return (subitems || []).map((s) => ({
    ...s,
    name: resolveName(s.name, { date: c.baseDate, parent: c.parent }),
    column_values: (s.column_values || []).map((cv) => resolveColumnValue(cv, c)).filter(Boolean),
  }));
}
